import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const AuthContext = createContext(null);

const SESSION_KEYS = {
  userId: 'userId',
  tenantId: 'tenantId',
  token: 'token',
  name: 'name',
  role: 'role',
  code: 'code',
  expiresAt: 'expiresAt',
  lastActivityAt: 'lastActivityAt',
};

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutos

export function AuthProvider({ children }) {
  // Hidratar de forma sincrónica para evitar perder sesión en F5
  const [userId, setUserId] = useState(() => sessionStorage.getItem(SESSION_KEYS.userId));
  const [tenantId, setTenantId] = useState(() => sessionStorage.getItem(SESSION_KEYS.tenantId) ?? sessionStorage.getItem('mandatoryId') ?? sessionStorage.getItem('mandatoyId'));
  const [token, setToken] = useState(() => sessionStorage.getItem(SESSION_KEYS.token) ?? sessionStorage.getItem('authToken'));
  const [name, setName] = useState(() => sessionStorage.getItem(SESSION_KEYS.name));
  const [role, setRole] = useState(() => sessionStorage.getItem(SESSION_KEYS.role));
  const [code, setCode] = useState(() => sessionStorage.getItem(SESSION_KEYS.code));
  const [expiresAt, setExpiresAt] = useState(() => sessionStorage.getItem(SESSION_KEYS.expiresAt));

  // Inactividad: auto-logout tras 5 minutos
  useEffect(() => {
    const markActivity = () => {
      sessionStorage.setItem(SESSION_KEYS.lastActivityAt, String(Date.now()));
    };

    // Inicializar último activity si no existe
    if (!sessionStorage.getItem(SESSION_KEYS.lastActivityAt)) {
      markActivity();
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));

    const interval = setInterval(() => {
      const ts = Number(sessionStorage.getItem(SESSION_KEYS.lastActivityAt) || '0');
      if (userId && tenantId && token && ts && Date.now() - ts > INACTIVITY_MS) {
        // Inactivo por más de 5 min: cerrar sesión
        logout();
      }
    }, 30 * 1000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, markActivity));
      clearInterval(interval);
    };
  }, [userId, tenantId, token]);

  const isAuthenticated = Boolean(userId && tenantId && token);

  const login = ({ userId: uid, tenantId: tid, token: tkn, name: nm, role: rl, code: cd, expiresAt: exp }) => {
    if (uid) {
      setUserId(uid);
      sessionStorage.setItem(SESSION_KEYS.userId, String(uid));
    }
    if (tid) {
      setTenantId(tid);
      sessionStorage.setItem(SESSION_KEYS.tenantId, String(tid));
    }
    if (tkn) {
      setToken(tkn);
      sessionStorage.setItem(SESSION_KEYS.token, String(tkn));
    }
    if (nm) {
      setName(nm);
      sessionStorage.setItem(SESSION_KEYS.name, String(nm));
    }
    if (rl != null) {
      setRole(String(rl));
      sessionStorage.setItem(SESSION_KEYS.role, String(rl));
    }
    if (cd != null) {
      setCode(String(cd));
      sessionStorage.setItem(SESSION_KEYS.code, String(cd));
    }
    if (exp != null) {
      setExpiresAt(String(exp));
      sessionStorage.setItem(SESSION_KEYS.expiresAt, String(exp));
    }
    // Marcar actividad al iniciar
    sessionStorage.setItem(SESSION_KEYS.lastActivityAt, String(Date.now()));
  };

  const logout = () => {
    setUserId(null);
    setTenantId(null);
    setToken(null);
    setName(null);
    setRole(null);
    setCode(null);
    setExpiresAt(null);
    sessionStorage.removeItem(SESSION_KEYS.userId);
    sessionStorage.removeItem(SESSION_KEYS.tenantId);
    sessionStorage.removeItem(SESSION_KEYS.token);
    sessionStorage.removeItem(SESSION_KEYS.name);
    sessionStorage.removeItem(SESSION_KEYS.role);
    sessionStorage.removeItem(SESSION_KEYS.code);
    sessionStorage.removeItem(SESSION_KEYS.expiresAt);
    sessionStorage.removeItem(SESSION_KEYS.lastActivityAt);
    sessionStorage.removeItem('mandatoyId');
    sessionStorage.removeItem('mandatoryId');
    sessionStorage.removeItem('authToken');
  };

  const value = useMemo(
    () => ({ userId, tenantId, token, name, role, code, expiresAt, isAuthenticated, login, logout }),
    [userId, tenantId, token, name, role, code, expiresAt, isAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
